// Anropen till molnet. Kakan för sessionen skickas av webbläsaren.

import type {
  Bat,
  Fardsida,
  Fardvisning,
  Forseglad,
  Givarroll,
  Guideforslag,
  Guidelage,
  Guidesvar,
  Gxinloggning,
  Halsa,
  Handelse,
  Hemlighet,
  Homeynyckel,
  Motor,
  Nattariff,
  Navsort,
  Oversikt,
  Plantyp,
  Planvisning,
  Platsinfo,
  Platsinstallningar,
  Platstyp,
  Roll,
  Svar,
  Uppstallning,
  Uppstallningsforbrukare,
  Utblaskurva,
  Varmeprofil,
  Varmeprofilvisning,
  Victronbank,
  Vistelse,
  Vistelsetyp,
} from "@batkoll/gemensam";
import { forsegla } from "@batkoll/gemensam";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

export class InteInloggad extends Error {}

export class Felsvar extends Error {
  readonly status: number;

  constructor(meddelande: string, status: number) {
    super(meddelande);
    this.status = status;
  }
}

async function begar<T>(url: string, init?: RequestInit, tillat401 = false): Promise<T> {
  const svar = await fetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    credentials: "same-origin",
  });
  if (svar.status === 401 && !tillat401) throw new InteInloggad("inte inloggad");
  if (!svar.ok) {
    const fel = (await svar.json().catch(() => ({}))) as { fel?: string };
    throw new Felsvar(fel.fel ?? `fel ${svar.status}`, svar.status);
  }
  return (await svar.json()) as T;
}

const post = <T>(url: string, kropp: unknown = {}, tillat401 = false) =>
  begar<T>(url, { method: "POST", body: JSON.stringify(kropp) }, tillat401);

export interface Jag {
  epost: string;
  prov: boolean;
  platser: Platsinfo[];
  /** Samma som platser, som i b2. */
  batar: Bat[];
  passkeys: number;
  pushEnheter: number;
  tystaTider: { fran: number; till: number };
}

export interface Konfig {
  provinloggning: boolean;
  vapidPublik: string | null;
}

/** En ny plats från appen (b5). */
export interface Nyplats {
  namn: string;
  typ: Platstyp;
  tidszon: string;
  position: { lat: number; lon: number } | null;
  nav: Navsort;
}

/** En ändring av en plats. Null tar bort ett värde eller återställer det till förvalet. */
export interface Platsandring {
  namn?: string;
  typ?: Platstyp;
  tidszon?: string;
  position?: { lat: number; lon: number } | null;
  elomrade?: string | null;
  elavtal?: string | null;
  paslag?: number | null;
  /** b6: nättariffen, påslaget per period (null: inget) och skattefaktorn (null: förvalet). */
  nattariff?: Nattariff;
  paslagP1?: number | null;
  paslagP2?: number | null;
  paslagP3?: number | null;
  skattefaktor?: number | null;
  forvarmningTimmar?: number | null;
  varmemodell?: Record<string, number | null>;
  /** I drift eller ej i drift (b6). */
  iDrift?: boolean;
  /** b8: gränserna för varningen för hård vind, för en båt. Null: förvalet. */
  vind?: { kulingMs?: number | null; byarMs?: number | null };
  /** b10: gränserna för kylskåpet och frysen (null: förvalet). */
  kylFrys?: {
    kylMaxGrader?: number | null;
    kylMinuter?: number | null;
    frysMaxGrader?: number | null;
    frysMinuter?: number | null;
  };
  /** b11 (BK33): vinterlägets gränser, för en båt (null: förvalet). */
  vinter?: {
    av?: number | null;
    pa?: number | null;
    varningUnder?: number | null;
    varningOver?: number | null;
  };
  /** b11: svartvattentankens gränser, för en båt (null: förvalet). */
  tankar?: { svartvattenVarning?: number | null; svartvattenAkut?: number | null };
  /** b11 (BK31): kortet Badtemperatur visas. */
  badtemperatur?: boolean;
  /** b15: dela okända enheter med e-VAtek (av som förval). */
  delaOkanda?: boolean;
  /** b12: motorernas instanser, dieseltankens volym och serviceintervallen (null: inget). */
  motorer?: {
    instansBabord?: number;
    instansStyrbord?: number;
    dieseltankL?: number | null;
    /** b13: sidornas tankar, och volymen per tank (null: ingen). */
    tankBabord?: string;
    tankStyrbord?: string;
    tankvolymer?: Record<string, number | null>;
    serviceBabord?: number | null;
    serviceStyrbord?: number | null;
    /** b14: hela uppställningen, som ägaren bekräftar eller ändrar, och servicen per motor. */
    uppstallning?: Uppstallning;
    service?: Partial<Record<Motor, number | null>>;
  };
  /** b12: gränserna för självsvängningen (null: förvalet). */
  pendling?: {
    kursGrader?: number | null;
    girGraderS?: number | null;
    slagsidaGrader?: number | null;
  };
  /** b13: gränserna för misstänkt is i väderstationen (null: förvalet). */
  vaderstation?: {
    isTempGrader?: number | null;
    isTimmar?: number | null;
    isPrognosMs?: number | null;
  };
  /** b14: jordgivarnas namn och gränser per kanal, för ett hus (null: förvalet). */
  jordgivare?: Record<string, { namn?: string | null; torr?: number | null; blot?: number | null }>;
  /** b16: Victron: GX-enhetens adress (null tar bort den) och bankerna per enhet. */
  victron?: {
    gx?: { adress: string | null; portal?: string | null };
    bankar?: Record<string, Victronbank>;
  };
  /** b16: förbrukarna i ett hus, med signalen eller utblåset. */
  forbrukare?: Uppstallningsforbrukare[];
  /** b17: Homeys adress, eller null för att ta bort den. */
  homey?: { adress: string | null };
  /** b20-2: ytterdörrens tider i timmar (17,5 är 17.30), för ett hus (null: förvalet). */
  ytterdorr?: {
    incheckning?: number | null;
    utcheckning?: number | null;
    stadEgen?: number | null;
    stadGast?: number | null;
  };
}

/** En rad i navets logg (b7). */
export interface Navloggrad {
  id: number;
  tid: number;
  rad: string;
}

/**
 * b16: förseglar en nyckel för en Victron-enhet till navets publika nyckel (gemensam/lada.ts).
 * Molnet ser bara lådan.
 */
export function forseglaNyckel(lada: string, fynd: string, nyckel: string) {
  return forsegla(lada, fynd, JSON.stringify({ nyckel: nyckel.trim().toLowerCase() }));
}

/**
 * b17: förseglar GX-enhetens användarnamn och lösenord, eller Homeys nyckel, till navets publika
 * nyckel. Ett tomt lösenord eller en tom nyckel tar bort det som navet har. Molnet ser bara lådan.
 */
export function forseglaHemlighet(
  lada: string,
  amne: Hemlighet,
  innehall: Gxinloggning | Homeynyckel,
): Promise<Forseglad> {
  return forsegla(lada, amne, JSON.stringify(innehall));
}

export const api = {
  konfig: () => begar<Konfig>("/api/v1/konfig"),
  jag: () => begar<Jag>("/api/v1/jag"),
  begarLank: (epost: string) =>
    post<{ ok: true; meddelande: string }>("/api/v1/inloggning/lank", { epost }),
  anvandLank: (token: string) => post<{ ok: true }>("/api/v1/inloggning/lank/klar", { token }),
  loggaIn: (epost: string, losenord: string) =>
    post<{ ok: true }>("/api/v1/inloggning", { epost, losenord }, true),
  loggaUt: () => post<{ ok: true }>("/api/v1/utloggning"),
  async loggaInMedPasskey(): Promise<void> {
    const alternativ = await post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(
      "/api/v1/inloggning/passkey/start",
    );
    const svar = await startAuthentication({ optionsJSON: alternativ });
    await post("/api/v1/inloggning/passkey/klar", { svar }, true);
  },
  async laggTillPasskey(): Promise<void> {
    const alternativ = await post<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
      "/api/v1/passkey/ny/start",
    );
    const svar = await startRegistration({ optionsJSON: alternativ });
    await post("/api/v1/passkey/ny/klar", { svar });
  },
  prenumerera: (p: PushSubscriptionJSON) => post<{ ok: true }>("/api/v1/push/prenumeration", p),
  avprenumerera: (endpoint: string) =>
    begar<{ ok: true }>("/api/v1/push/prenumeration", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),
  oversikt: () => begar<Oversikt>("/api/v1/oversikt"),
  halsa: (plats: string) => begar<Halsa>(`/api/v1/platser/${plats}/halsa`),
  handelser: (plats: string) => begar<Handelse[]>(`/api/v1/platser/${plats}/handelser`),
  svara: (plats: string, id: string, svar: Svar) =>
    post<{ ok: true }>(`/api/v1/platser/${plats}/handelser/${id}/svar`, { svar }),
  /** "Åka till" och "Resa bort". Tiderna är lokal tid på platsen, till exempel 2026-10-23T18:00. */
  resa: (typ: Plantyp, platser: string[], fran: string, till: string | null) =>
    post<{ planer: { plats: { id: string; namn: string }; plan: Planvisning }[] }>(
      "/api/v1/resor",
      { typ, platser, fran, till },
    ),
  taBortPlan: (plats: string, plan: string) =>
    begar<{ ok: true }>(`/api/v1/platser/${plats}/planer/${plan}`, { method: "DELETE" }),
  // b5: platserna och inställningarna, bara för ägaren.
  skapaPlats: (ny: Nyplats) =>
    post<{ plats: { id: string; namn: string }; nyckel: string; sort: Navsort }>(
      "/api/v1/platser",
      ny,
    ),
  installningar: (plats: string) =>
    begar<Platsinstallningar>(`/api/v1/platser/${plats}/installningar`),
  // b24: värmen vid ankomst och avresa, bara för ägaren.
  varmeprofil: (plats: string) => begar<Varmeprofilvisning>(`/api/v1/platser/${plats}/varmeprofil`),
  sattVarmeprofil: (plats: string, profil: Varmeprofil) =>
    begar<Varmeprofilvisning>(`/api/v1/platser/${plats}/varmeprofil`, {
      method: "PUT",
      body: JSON.stringify(profil),
    }),
  andraPlats: (plats: string, andring: Platsandring) =>
    begar<Platsinstallningar>(`/api/v1/platser/${plats}/installningar`, {
      method: "PATCH",
      body: JSON.stringify(andring),
    }),
  /** Styrningen i molnet. Appen har visat bekräftelsen. */
  styrning: (plats: string, pa: boolean) =>
    post<Platsinstallningar>(`/api/v1/platser/${plats}/styrning`, { pa, bekraftat: true }),
  /** b10: vinterläget, fas 2, på eller av. */
  vinterlage: (plats: string, pa: boolean) =>
    post<Platsinstallningar>(`/api/v1/platser/${plats}/vinterlage`, { pa, bekraftat: true }),
  /** b10: fas 1, laddaren av eller på för en rad i vitlistan. */
  laddare: (plats: string, rad: string, pa: boolean) =>
    post<Platsinstallningar>(`/api/v1/platser/${plats}/vinterlage/laddare`, {
      rad,
      pa,
      bekraftat: true,
    }),
  /** Navets logg, de senaste raderna först. `fore`: rader före den. */
  navlogg: (plats: string, sort: Navsort, fore?: number) =>
    begar<{ rader: Navloggrad[]; fler: boolean }>(
      `/api/v1/platser/${plats}/nav/${sort}/logg${fore ? `?fore=${fore}` : ""}`,
    ),
  nyttNav: (plats: string, sort: Navsort) =>
    post<{ nyckel: string; sort: Navsort }>(`/api/v1/platser/${plats}/nav`, { sort }),
  /** b8: väljer en Ruuvi-givare och dess plats, eller tar bort valet. */
  valjGivare: (plats: string, givare: string, roll: Givarroll, namn: string | null) =>
    begar<Platsinstallningar>(`/api/v1/platser/${plats}/givare/${encodeURIComponent(givare)}`, {
      method: "PUT",
      body: JSON.stringify({ roll, namn }),
    }),
  taBortGivare: (plats: string, givare: string) =>
    begar<Platsinstallningar>(`/api/v1/platser/${plats}/givare/${encodeURIComponent(givare)}`, {
      method: "DELETE",
    }),
  // b20-2: vistelserna, för ytterdörrens notiser. Datumen är platsens, till exempel 2026-11-02.
  vistelser: (plats: string) => begar<Vistelse[]>(`/api/v1/platser/${plats}/vistelser`),
  laggTillVistelse: (plats: string, v: { ankomst: string; avresa: string; typ: Vistelsetyp }) =>
    post<Vistelse[]>(`/api/v1/platser/${plats}/vistelser`, v),
  taBortVistelse: (plats: string, vistelse: string) =>
    begar<Vistelse[]>(`/api/v1/platser/${plats}/vistelser/${encodeURIComponent(vistelse)}`, {
      method: "DELETE",
    }),
  // b12: båtens resor, förbrukningen, provkörningarna och servicen.
  farder: (plats: string) => begar<Fardvisning>(`/api/v1/platser/${plats}/farder`),
  fard: (plats: string, fard: string) => begar<Fardsida>(`/api/v1/platser/${plats}/farder/${fard}`),
  startaProvkorning: (plats: string, etikett: string) =>
    post<Fardvisning>(`/api/v1/platser/${plats}/provkorningar`, { etikett }),
  stoppaProvkorning: (plats: string, prov: string) =>
    post<Fardvisning>(`/api/v1/platser/${plats}/provkorningar/${prov}/stopp`),
  /** Kvitterar en service, vid timräknaren nu eller vid de timmar som skrivs in. */
  kvitteraService: (plats: string, motor: string, timmar: number | null) =>
    post<Fardvisning>(`/api/v1/platser/${plats}/service`, { motor, timmar }),
  /** b13: "Tankat X liter i babords tank". Tanken är en sida eller tankens instans. */
  tankning: (plats: string, tank: string, liter: number) =>
    post<Fardvisning>(`/api/v1/platser/${plats}/tankningar`, { tank, liter }),
  /** b13: nollställer tankens tabell. */
  nollstallTabell: (plats: string, tank: string) =>
    post<{ ok: true }>(`/api/v1/platser/${plats}/tankar/${encodeURIComponent(tank)}/nollstall`),
  // b15: guiden "Kom igång".
  guiden: (plats: string) => begar<Guidelage>(`/api/v1/platser/${plats}/guiden`),
  guideForslag: (plats: string, svar: Guidesvar) =>
    post<Guideforslag>(`/api/v1/platser/${plats}/guiden/forslag`, { svar }),
  guideBekrafta: (plats: string, svar: Guidesvar, nyckel: string) =>
    post<{ antal: number; meddelanden: string[]; installningar: Platsinstallningar }>(
      `/api/v1/platser/${plats}/guiden/bekrafta`,
      { svar, nyckel },
    ),
  // b16: Victron, värmarna efter utblåset och de tysta tiderna.
  /**
   * Förseglar nyckeln för en Victron-enhet till navet i webbläsaren, och skickar bara lådan.
   * Nyckeln lämnar aldrig webbläsaren i klartext.
   */
  hemlighet: async (
    plats: string,
    amne: Hemlighet,
    lada: string,
    innehall: Gxinloggning | Homeynyckel,
  ) =>
    post<Platsinstallningar>(`/api/v1/platser/${plats}/hemlighet/${amne}`, {
      lada: await forseglaHemlighet(lada, amne, innehall),
    }),
  victronNyckel: async (plats: string, fynd: string, lada: string, nyckel: string) =>
    post<Platsinstallningar>(`/api/v1/platser/${plats}/victron/nyckel`, {
      fynd,
      lada: await forseglaNyckel(lada, fynd, nyckel),
    }),
  utblaskurva: (plats: string, nyckel: string) =>
    begar<Utblaskurva>(`/api/v1/platser/${plats}/forbrukare/${nyckel}/kurva`),
  tystaTider: (fran: number, till: number) =>
    begar<{ tystaTider: { fran: number; till: number }; andrad: boolean }>(
      "/api/v1/jag/tysta-tider",
      {
        method: "PUT",
        body: JSON.stringify({ fran, till }),
      },
    ),
  bjudIn: (plats: string, epost: string, roll: Roll) =>
    post<{ meddelande: string; installningar: Platsinstallningar }>(
      `/api/v1/platser/${plats}/konton`,
      { epost, roll },
    ),
  /** Tar bort ett konto från platsen. Tar ägaren bort sig själv kommer bara { borta: true }. */
  taBortKonto: (plats: string, konto: string) =>
    begar<Platsinstallningar | { borta: true }>(`/api/v1/platser/${plats}/konton/${konto}`, {
      method: "DELETE",
    }),
};
